import React, { useEffect, useState } from "react";
import { Box, Button, Grid, Paper, Typography, useTheme } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DeleteEmployeeModal from "./DeleteEmployeeModal";
import { useParams } from "react-router-dom";
import { useGetEmployeeDetail } from "../../hooks/api/useEmployee";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation,
  usePlatformConfig
} from "@kivunova/kivufrontendcommon";
import UpdateEmployeeModal from "./EditEmployeeModal";
import NoEmployeeDetailFound from "./NoEmployeeDetailFound";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import { formatUserRoleLabel } from "../Profile/utils/userRoleLabels";

const EmployeeDetailPage: React.FC = () => {
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const { employeeId } = useParams<{ employeeId: string }>();
  const employee = useSelector(
    (state: RootState) => state.employees.employeeDetail
  );

  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const getEmployeeDetailMutation = useGetEmployeeDetail();
  const { t } = useKivunovaTranslation();
  const { config } = usePlatformConfig();

  useEffect(() => {
    (async () => {
      try {
        if (employeeId) {
          await getEmployeeDetailMutation.mutateAsync({ userId: employeeId });
        }
      } catch (e) {
        console.error("Failed to fetch employee details", e);
      }
    })();
  }, [employeeId]);

  if (getEmployeeDetailMutation.isPending) {
    return (
      <CommonLoader
        loadingMessage={t(
          "employee-fetching-details",
          "Fetching employee details..."
        )}
      />
    );
  }

  if (!employee) {
    return <NoEmployeeDetailFound />;
  }

  const fields: [string, string | undefined | number][] = [
    [t("employee-first-name", "First Name"), employee.firstName],
    [t("employee-last-name", "Last Name"), employee.lastName],
    [t("employee-email", "Email"), employee.email],
    [t("employee-phone", "Phone"), employee.phone],
    [
      t("employee-role", "Role"),
      formatUserRoleLabel(employee.role, t, config?.businessType)
    ],
    [t("employee-company-id", "Company ID"), employee.id],
    [t("employee-store-id", "Store ID"), employee.id],
    [t("employee-account-status", "Account Status"), employee.accountStatus],
    [
      t("employee-profile-image", "Profile Image"),
      employee.profileImage
        ? t("employee-profile-image-available", "Available")
        : t("employee-profile-image-not-uploaded", "Not Uploaded")
    ]
  ];

  return (
    <Box sx={{ color: "black" }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection={matchMD ? "column" : "row"}
        alignItems="center"
        mb={3}
        flexWrap="wrap"
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          textAlign={matchMD ? "center" : "left"}
        >
          {employee.firstName} {employee.lastName}
        </Typography>

        <Box
          display="flex"
          justifyContent={matchMD ? "center" : "flex-end"}
          alignItems="center"
          width={matchMD ? "100%" : "auto"}
          gap={2}
          mt={{ xs: 2, md: 0 }}
        >
          <Button
            variant="text"
            startIcon={<EditOutlinedIcon />}
            onClick={() => setOpenEdit(true)}
            sx={{
              backgroundColor: "white",
              color: BRAND_COLOR,
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                opacity: 0.8,
                color: "white"
              },
              textTransform: "none"
            }}
          >
            {t("edit", "Edit")}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineOutlinedIcon />}
            onClick={() => setOpenDelete(true)}
            sx={{
              textTransform: "none"
            }}
          >
            {t("delete", "Delete")}
          </Button>
        </Box>
      </Box>

      {/* Detail Card */}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          borderRadius: 2,
          overflow: "hidden"
        }}
      >
        <Grid container>
          {fields.map(([label, value], index) => (
            <React.Fragment key={label}>
              <Grid
                sx={{
                  width: "100%",
                  padding: "0 10px",
                  display: "flex",
                  borderBottom:
                    index === fields.length - 1 ? "none" : "1px solid #e0e0e0"
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    borderRight: "0.5px solid #e0e0e0",
                    p: 1.5,
                    bgcolor: "white"
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="text.secondary"
                  >
                    {label}:
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 2,
                    p: 1.5,
                    bgcolor: "white"
                  }}
                >
                  <Typography variant="body2">{value ?? "-"}</Typography>
                </Box>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Paper>

      {/* Modals */}
      <UpdateEmployeeModal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        employee={employee}
      />
      <DeleteEmployeeModal
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        employee={employee}
      />
    </Box>
  );
};

export default EmployeeDetailPage;
